import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { meetingAPI, resolutionAPI, certificateAPI, aiAPI, organizationAPI, templateAPI } from '../api';
import { Card, CardHeader, CardTitle, CardContent, CardFooter, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { format, parseISO } from 'date-fns';
import { 
  ArrowLeft, Award, FileText, Download, Loader2, 
  Sparkles, CheckCircle, ChevronRight, Building2, Calendar
} from 'lucide-react';
import { toast } from 'sonner';

const CertificateWizard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedMeetingId = searchParams.get('meeting_id');
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [organization, setOrganization] = useState(null);
  const [meetings, setMeetings] = useState([]);
  const [resolutions, setResolutions] = useState([]);
  
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [selectedResolutions, setSelectedResolutions] = useState([]);
  const [certificateType, setCertificateType] = useState('single');
  const [purpose, setPurpose] = useState('');
  const [generatedContent, setGeneratedContent] = useState('');
  const [editableContent, setEditableContent] = useState('');

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (selectedMeeting) {
      fetchResolutions(selectedMeeting.id);
    }
  }, [selectedMeeting]);

  const fetchInitialData = async () => {
    try {
      const [orgRes, meetingsRes] = await Promise.all([
        organizationAPI.get(user.organization_id),
        meetingAPI.getAll({ status: 'completed' }),
      ]);
      
      setOrganization(orgRes.data);
      setMeetings(meetingsRes.data);
      
      if (preselectedMeetingId) {
        const meeting = meetingsRes.data.find(m => m.id === preselectedMeetingId);
        if (meeting) {
          setSelectedMeeting(meeting);
        }
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchResolutions = async (meetingId) => {
    try {
      const response = await resolutionAPI.getAll({ meeting_id: meetingId });
      setResolutions(response.data);
    } catch (err) {
      console.error('Failed to fetch resolutions:', err);
    }
  };

  const toggleResolution = (resolution) => {
    if (certificateType === 'single') {
      setSelectedResolutions([resolution]);
    } else {
      if (selectedResolutions.find(r => r.id === resolution.id)) {
        setSelectedResolutions(selectedResolutions.filter(r => r.id !== resolution.id));
      } else {
        setSelectedResolutions([...selectedResolutions, resolution]);
      }
    }
  };

  const generateCertificate = async () => {
    if (!organization?.ai_enabled) {
      // Try template first, then basic fallback
      const content = await generateWithTemplate();
      setGeneratedContent(content);
      setEditableContent(content);
      setStep(4);
      return;
    }
    
    setGenerating(true);
    try {
      const facts = {
        meeting_id: selectedMeeting.id,
        meeting_title: selectedMeeting.title,
        meeting_date: format(parseISO(selectedMeeting.date_time), 'MMMM d, yyyy'),
        meeting_venue: selectedMeeting.venue || 'Virtual Meeting',
        quorum_details: selectedMeeting.quorum_achieved 
          ? `Quorum was present with ${selectedMeeting.quorum_value || 'a majority'} of members in attendance`
          : 'Meeting properly convened',
        resolutions: selectedResolutions.map(r => ({
          number: r.resolution_number,
          title: r.title,
          text: r.text
        })),
        purpose: purpose
      };
      
      const response = await aiAPI.draftCertificate({
        facts,
        type: certificateType
      });
      
      setGeneratedContent(response.data.draft);
      setEditableContent(response.data.draft);
      setStep(4);
    } catch (err) {
      toast.error('Failed to generate certificate with AI. Using template/basic fallback.');
      const content = await generateWithTemplate();
      setGeneratedContent(content);
      setEditableContent(content);
      setStep(4);
    } finally {
      setGenerating(false);
    }
  };

  const generateWithTemplate = async () => {
    try {
      const response = await templateAPI.generateFromTemplate({
        template_type: 'certificate',
        meeting_id: selectedMeeting?.id,
      });
      if (response.data?.found && response.data?.html) {
        // Template found — inject resolution details
        const resolutionList = selectedResolutions.map(r => 
          `<p><strong>Resolution No. ${r.resolution_number}</strong></p>
<p>${r.title}</p>
<p style="margin-left: 20px;">${r.text || 'RESOLVED, as stated above.'}</p>`
        ).join('\n\n');
        const html = response.data.html + `\n<div style="margin-top:20px;">${resolutionList}</div>`;
        toast.success(`Template "${response.data.template_name}" applied`);
        return html;
      }
    } catch (err) {
      // Template fetch failed, fall through
    }
    return generateBasicCertificate();
  };

  const generateBasicCertificate = () => {
    const resolutionList = selectedResolutions.map((r, idx) => 
      `<p><strong>Resolution No. ${r.resolution_number}</strong></p>
<p>${r.title}</p>
<p style="margin-left: 20px;">${r.text || 'RESOLVED, as stated above.'}</p>`
    ).join('\n\n');
    
    return `<div style="font-family: 'Times New Roman', serif; padding: 40px;">
<h1 style="text-align: center; font-size: 18px; margin-bottom: 30px;">SECRETARY'S CERTIFICATE</h1>

<p>I, <strong>[CORPORATE SECRETARY NAME]</strong>, Filipino, of legal age, and the duly elected and qualified Corporate Secretary of <strong>${organization?.name || '[COMPANY NAME]'}</strong>, a corporation duly organized and existing under and by virtue of the laws of the Republic of the Philippines, with principal office address at [ADDRESS], do hereby certify that:</p>

<p>1. A ${selectedMeeting?.basis === 'special' ? 'special' : 'regular'} meeting of the Board of Directors of the Corporation was held on <strong>${selectedMeeting ? format(parseISO(selectedMeeting.date_time), 'MMMM d, yyyy') : '[DATE]'}</strong> at ${selectedMeeting?.venue || '[VENUE]'}, at which meeting a quorum was present and acting throughout;</p>

<p>2. During the said meeting, the following resolution${selectedResolutions.length > 1 ? 's were' : ' was'} unanimously approved:</p>

${resolutionList}

<p>3. The foregoing resolution${selectedResolutions.length > 1 ? 's remain' : ' remains'} in full force and effect as of the date hereof and ${selectedResolutions.length > 1 ? 'have' : 'has'} not been amended, modified, or revoked.</p>

<p>IN WITNESS WHEREOF, I have hereunto set my hand and affixed the corporate seal of the Corporation this _____ day of _____________, ${new Date().getFullYear()} at _________________, Philippines.</p>

<div style="margin-top: 60px; text-align: center;">
<p>_________________________________</p>
<p><strong>[CORPORATE SECRETARY NAME]</strong></p>
<p>Corporate Secretary</p>
</div>

<div style="margin-top: 40px;">
<p><strong>SUBSCRIBED AND SWORN</strong> to before me this _____ day of _____________, ${new Date().getFullYear()}, affiant exhibiting to me competent evidence of identity being _________________________ issued on _____________ at _____________.</p>

<div style="margin-top: 40px; text-align: center;">
<p>_________________________________</p>
<p>Notary Public</p>
<p>Doc. No. _____;</p>
<p>Page No. _____;</p>
<p>Book No. _____;</p>
<p>Series of ${new Date().getFullYear()}.</p>
</div>
</div>
</div>`;
  };

  const saveCertificate = async () => {
    setSaving(true);
    try {
      const response = await certificateAPI.create({
        organization_id: user.organization_id,
        title: `Secretary's Certificate - ${selectedResolutions.map(r => r.resolution_number).join(', ')}`,
        meeting_id: selectedMeeting.id,
        resolution_ids: selectedResolutions.map(r => r.id)
      });
      
      // Update with content
      await certificateAPI.update(response.data.id, {
        body_html: editableContent,
        purpose: purpose,
        facts: {
          meeting_id: selectedMeeting.id,
          resolution_numbers: selectedResolutions.map(r => r.resolution_number),
          generated_at: new Date().toISOString()
        }
      });
      
      toast.success('Certificate saved successfully');
      navigate('/records?tab=certificate');
    } catch (err) {
      toast.error('Failed to save certificate');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-[#00A3A3]" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="certificate-wizard">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-[#1A1D21]">Secretary's Certificate</h1>
          <p className="text-[#5A6570] mt-1">Generate a certificate for board resolutions</p>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center gap-2">
        {[1, 2, 3, 4].map((s) => (
          <React.Fragment key={s}>
            <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
              s === step 
                ? 'bg-[#00A3A3] text-white' 
                : s < step 
                ? 'bg-green-100 text-green-800'
                : 'bg-[#F0F2F4] text-[#5A6570]'
            }`}>
              {s < step ? <CheckCircle className="h-4 w-4" /> : s}
            </div>
            {s < 4 && <div className={`flex-1 h-1 rounded ${s < step ? 'bg-green-200' : 'bg-[#E2E6EA]'}`} />}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1: Select Meeting */}
      {step === 1 && (
        <Card className="border-[#E2E6EA]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-[#00A3A3]" />
              Step 1: Select Meeting
            </CardTitle>
            <CardDescription>Choose the meeting containing the resolution(s) to certify</CardDescription>
          </CardHeader>
          <CardContent>
            {meetings.length === 0 ? (
              <div className="text-center py-8 text-[#5A6570]">
                <Calendar className="h-10 w-10 mx-auto mb-3 text-[#A8B1B9]" />
                <p>No completed meetings found</p>
              </div>
            ) : (
              <div className="space-y-2">
                {meetings.map((meeting) => (
                  <button
                    key={meeting.id}
                    onClick={() => setSelectedMeeting(meeting)}
                    className={`w-full text-left p-4 rounded-lg border transition-colors ${
                      selectedMeeting?.id === meeting.id
                        ? 'border-[#00A3A3] bg-[#00A3A3]/5'
                        : 'border-[#E2E6EA] hover:border-[#00A3A3]'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-[#1A1D21]">{meeting.title}</p>
                        <p className="text-sm text-[#5A6570]">
                          {format(parseISO(meeting.date_time), 'MMMM d, yyyy')}
                        </p>
                      </div>
                      <ChevronRight className="h-5 w-5 text-[#A8B1B9]" />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
          <CardFooter className="border-t border-[#E2E6EA] bg-[#F8F9FA]">
            <Button
              onClick={() => setStep(2)}
              disabled={!selectedMeeting}
              className="ml-auto bg-[#00A3A3] hover:bg-[#008585]"
            >
              Continue
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </CardFooter>
        </Card>
      )}

      {/* Step 2: Select Resolutions */}
      {step === 2 && (
        <Card className="border-[#E2E6EA]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="h-5 w-5 text-[#F58A26]" />
              Step 2: Select Resolution(s)
            </CardTitle>
            <CardDescription>Choose the resolution(s) to include in the certificate</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-4">
              <Button
                variant={certificateType === 'single' ? 'default' : 'outline'}
                onClick={() => {
                  setCertificateType('single');
                  setSelectedResolutions([]);
                }}
                className={certificateType === 'single' ? 'bg-[#00A3A3]' : 'border-[#E2E6EA]'}
              >
                Single Resolution
              </Button>
              <Button
                variant={certificateType === 'multiple' ? 'default' : 'outline'}
                onClick={() => {
                  setCertificateType('multiple');
                  setSelectedResolutions([]);
                }}
                className={certificateType === 'multiple' ? 'bg-[#00A3A3]' : 'border-[#E2E6EA]'}
              >
                Multiple Resolutions
              </Button>
            </div>

            {resolutions.length === 0 ? (
              <div className="text-center py-8 text-[#5A6570]">
                <Award className="h-10 w-10 mx-auto mb-3 text-[#A8B1B9]" />
                <p>No resolutions found for this meeting</p>
              </div>
            ) : (
              <div className="space-y-2">
                {resolutions.map((resolution) => (
                  <div
                    key={resolution.id}
                    onClick={() => toggleResolution(resolution)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleResolution(resolution); }}
                    className={`w-full text-left p-4 rounded-lg border transition-colors cursor-pointer ${
                      selectedResolutions.find(r => r.id === resolution.id)
                        ? 'border-[#F58A26] bg-[#F58A26]/5'
                        : 'border-[#E2E6EA] hover:border-[#F58A26]'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <Checkbox
                        checked={!!selectedResolutions.find(r => r.id === resolution.id)}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <p className="font-mono text-sm text-[#00A3A3]">{resolution.resolution_number}</p>
                        <p className="font-medium text-[#1A1D21]">{resolution.title}</p>
                        {resolution.text && (
                          <p className="text-sm text-[#5A6570] mt-1 line-clamp-2">{resolution.text}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
          <CardFooter className="border-t border-[#E2E6EA] bg-[#F8F9FA] flex justify-between">
            <Button variant="outline" onClick={() => setStep(1)} className="border-[#E2E6EA]">
              Back
            </Button>
            <Button
              onClick={() => setStep(3)}
              disabled={selectedResolutions.length === 0}
              className="bg-[#00A3A3] hover:bg-[#008585]"
            >
              Continue
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </CardFooter>
        </Card>
      )}

      {/* Step 3: Purpose & Generate */}
      {step === 3 && (
        <Card className="border-[#E2E6EA]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-[#00A3A3]" />
              Step 3: Certificate Details
            </CardTitle>
            <CardDescription>Add purpose and generate the certificate</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="purpose">Purpose of Certificate</Label>
              <Input
                id="purpose"
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
                placeholder="e.g., Bank requirement, SEC filing, Contract execution"
                className="mt-1 border-[#E2E6EA]"
              />
            </div>

            <div className="p-4 bg-[#F8F9FA] rounded-lg">
              <h4 className="font-medium text-[#1A1D21] mb-2">Summary</h4>
              <div className="space-y-2 text-sm text-[#5A6570]">
                <p><strong>Meeting:</strong> {selectedMeeting?.title}</p>
                <p><strong>Date:</strong> {selectedMeeting && format(parseISO(selectedMeeting.date_time), 'MMMM d, yyyy')}</p>
                <p><strong>Resolution(s):</strong> {selectedResolutions.map(r => r.resolution_number).join(', ')}</p>
              </div>
            </div>

            {organization?.ai_enabled && (
              <div className="flex items-center gap-3 p-4 bg-[#00A3A3]/5 border border-[#00A3A3]/20 rounded-lg">
                <Sparkles className="h-5 w-5 text-[#00A3A3]" />
                <div className="flex-1">
                  <p className="font-medium text-[#1A1D21]">AI-Assisted Generation</p>
                  <p className="text-xs text-[#5A6570]">Claude AI will help draft certificate content. <strong>This is a drafting aid only</strong> — the Corporate Secretary must review and formally certify all content before it becomes official.</p>
                </div>
              </div>
            )}
          </CardContent>
          <CardFooter className="border-t border-[#E2E6EA] bg-[#F8F9FA] flex justify-between">
            <Button variant="outline" onClick={() => setStep(2)} className="border-[#E2E6EA]">
              Back
            </Button>
            <Button
              onClick={generateCertificate}
              disabled={generating}
              className="bg-[#F58A26] hover:bg-[#D67520]"
            >
              {generating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  {organization?.ai_enabled && <Sparkles className="h-4 w-4 mr-2" />}
                  Generate Certificate
                </>
              )}
            </Button>
          </CardFooter>
        </Card>
      )}

      {/* Step 4: Review & Export */}
      {step === 4 && (
        <Card className="border-[#E2E6EA]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Step 4: Review & Finalize
            </CardTitle>
            <CardDescription>Review and edit the certificate before saving</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="border border-[#E2E6EA] rounded-lg p-4 bg-white min-h-[400px] overflow-auto">
              <div 
                className="prose prose-sm max-w-none font-document"
                contentEditable
                onInput={(e) => setEditableContent(e.currentTarget.innerHTML)}
                dangerouslySetInnerHTML={{ __html: editableContent }}
              />
            </div>
          </CardContent>
          <CardFooter className="border-t border-[#E2E6EA] bg-[#F8F9FA] flex justify-between">
            <Button variant="outline" onClick={() => setStep(3)} className="border-[#E2E6EA]">
              Back
            </Button>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  // Export as HTML/print
                  const printWindow = window.open('', '_blank');
                  printWindow.document.write(`
                    <html><head><title>Secretary's Certificate</title></head>
                    <body>${editableContent}</body></html>
                  `);
                  printWindow.document.close();
                  printWindow.print();
                }}
                className="border-[#E2E6EA]"
              >
                <Download className="h-4 w-4 mr-2" />
                Print / Export
              </Button>
              <Button
                onClick={saveCertificate}
                disabled={saving}
                className="bg-[#00A3A3] hover:bg-[#008585]"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Save Certificate
                  </>
                )}
              </Button>
            </div>
          </CardFooter>
        </Card>
      )}
    </div>
  );
};

export default CertificateWizard;
